import React from "react";
import { ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from "@mui/material";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const stopEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Secondary contextual menu for V4 Saved Search row Edit (pencil).
 * Actions: Rename, Permanently delete.
 */
export default function SavedSearchEditSubmenu({
  anchorEl = null,
  open = false,
  savedSearch = null,
  onClose,
  onRename,
  onDelete,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const name = savedSearch?.name ? String(savedSearch.name) : "saved search";

  const runAction = (event, action) => {
    stopEvent(event);
    action?.(savedSearch, event);
    onClose?.(event, "action");
  };

  return (
    <Menu
      id="saved-search-edit-submenu"
      data-testid="saved-search-edit-submenu"
      anchorEl={anchorEl}
      open={Boolean(open && anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            ml: 0.5,
            minWidth: 188,
            borderRadius: "12px",
            border: `1px solid ${
              isDark ? "rgba(255,255,255,0.12)" : "rgba(88,89,91,0.12)"
            }`,
            boxShadow: isDark
              ? "0 18px 40px rgba(0, 0, 0, 0.55)"
              : "0 18px 40px rgba(23, 15, 74, 0.14)",
            backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
            py: 0.5,
          },
        },
      }}
      MenuListProps={{
        "aria-label": `Edit actions for ${name}`,
        dense: true,
        autoFocusItem: true,
        sx: { py: 0.5 },
      }}
    >
      <MenuItem
        data-testid="saved-search-edit-rename"
        onClick={(event) => runAction(event, onRename)}
        onMouseDown={stopEvent}
        sx={{
          mx: 0.5,
          borderRadius: "8px",
          fontSize: 13,
          fontWeight: 550,
          minHeight: 36,
          "&:hover, &:focus": {
            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(74,44,245,0.06)",
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <DriveFileRenameOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary="Rename"
          secondary="Save filter changes as a new saved search."
          primaryTypographyProps={{ fontSize: 13, fontWeight: 550 }}
          secondaryTypographyProps={{ fontSize: 11.5 }}
        />
      </MenuItem>
      <MenuItem
        data-testid="saved-search-edit-delete"
        onClick={(event) => runAction(event, onDelete)}
        onMouseDown={stopEvent}
        sx={{
          mx: 0.5,
          borderRadius: "8px",
          fontSize: 13,
          fontWeight: 550,
          minHeight: 36,
          color: theme.palette.error.main,
          "&:hover, &:focus": {
            backgroundColor: isDark ? "rgba(211,47,47,0.16)" : "rgba(211,47,47,0.08)",
          },
          "& .MuiListItemIcon-root": { color: "inherit" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <DeleteOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 550 }}>
          Permanently delete
        </ListItemText>
      </MenuItem>
    </Menu>
  );
}
